import React from 'react';
import { Modal, Form, Select, message } from 'antd';
import { useTranslation } from '@refinedev/core';
import useAPI, { useGet } from '@/api/useApi';
import { endPoints } from '@/api/configs';
import { AdminRes } from '@/api/types/getAdmin';
import { GetReq } from '@/api/types/getReq';
import { getRequestErrorMessage } from '@/utils/requestPermissions';
import { extractApiListData } from '@/utils/parseNotificationsResponse';

interface AssignRequestModalProps {
  open: boolean;
  request: Pick<GetReq, 'id' | 'post_title'> | undefined;
  onClose: () => void;
  refetch: () => void;
}

interface AssignFormValues {
  admin_id: number;
}

export const AssignRequestModal: React.FC<AssignRequestModalProps> = ({
  open,
  request,
  onClose,
  refetch,
}) => {
  const { translate } = useTranslation();
  const [form] = Form.useForm<AssignFormValues>();
  const { usePost } = useAPI();

  const { data: adminsData, isLoading: isAdminsLoading } = useGet<AdminRes[]>(
    [endPoints.admin(), 'assign-picker', open],
    endPoints.admin(),
    {
      params: {
        page: 1,
        page_size: 100,
      },
    },
    {
      enabled: open,
    }
  );

  const { mutate: assignRequest, isLoading: isAssigning } = usePost(
    endPoints.assignRequest(String(request?.id))
  );

  const handleOk = async () => {
    if (!request) {
      return;
    }

    try {
      const values = await form.validateFields();
      assignRequest(
        { admin_id: values.admin_id },
        {
          onSuccess: () => {
            message.success(translate('listing-request.assign.success'));
            form.resetFields();
            onClose();
            refetch();
          },
          onError: (error) => {
            message.error(getRequestErrorMessage(error, translate));
          },
        }
      );
    } catch {
      // validation failed
    }
  };

  const handleCancel = () => {
    form.resetFields();
    onClose();
  };

  const adminOptions = extractApiListData<AdminRes>(adminsData)
    .filter((admin) => admin.account?.is_active !== false)
    .map((admin) => ({
      label: `${admin.name} — ${translate(`listing-admins.role.${admin.admin_type}`)}`,
      value: admin.id,
    }));

  return (
    <Modal
      title={translate('listing-request.assign.modal.title')}
      open={open}
      onOk={handleOk}
      onCancel={handleCancel}
      confirmLoading={isAssigning}
      destroyOnClose
    >
      {request?.post_title && (
        <p className="mb-4 text-gray-600">
          {translate('listing-request.postTitle.title')}: {request.post_title}
        </p>
      )}
      <Form form={form} layout="vertical">
        <Form.Item
          name="admin_id"
          label={translate('listing-request.assign.modal.selectAdmin')}
          rules={[
            {
              required: true,
              message: translate('pages.errors.required'),
            },
          ]}
        >
          <Select
            showSearch
            placeholder={translate('listing-request.assign.modal.selectAdmin')}
            options={adminOptions}
            loading={isAdminsLoading}
            optionFilterProp="label"
          />
        </Form.Item>
      </Form>
    </Modal>
  );
};
